import * as THREE from 'three'

import Config from '@glass-designer/config'
import BaseObject from './baseObject'
import MathsUtils from '@/utils/mathsUtils'
import GlassDesignerTypes from '../types'


// Editing Cutout
// A cutout object in the scene to display a cutout when dragging into the scene from the library
export default class EditingCutout extends BaseObject {
  constructor(editor, options = {}) {
    super(editor)
    this.objectClassName = 'EditingCutout'

    this.position = new THREE.Vector2(0, 0)
    this.angle = 0    // Degrees
    this.scale = new THREE.Vector2(1, 1)      // Used to mirror/flip the cutout

    // Link to the GlassBaseHole objects that contains the hole data
    this.holeInfoList = []

    // Datum links used while editing
    // Used to update the dynamic dimensions while adding/moving the cutout
    this.datumLinks = []

    this.bounds = new THREE.Box2()
    this.outlineGeom = null
    this.shapeObj = null

    this.sceneObject = new THREE.Object3D()
  }

  // onBeforeRender
  // Called prior to rendering of the object
  onBeforeRender() {
    super.onBeforeRender()

    if(this.shapeObj) {
      this.shapeObj.position.set(this.position.x, this.position.y, 80)

      let angleRads = MathsUtils.degreesToRadians(this.angle)
      let objAngles = new THREE.Euler(0, 0, angleRads)
      this.shapeObj.setRotationFromEuler(objAngles)
      this.shapeObj.scale.set(this.scale.x, this.scale.y, 1.0)
    }
  }


  // Update the position of the cutout
  setPosition(pos) {
    this.position.set(pos.x, pos.y)
  }

  // Update the rotation
  // In degrees
  setRotation(angle) {
    this.angle = angle
  }

  // Update the scale of the cutout
  setScale(scale) {
    this.scale.set(scale.x, scale.y)
  }

  // Set the datum links
  setDatumLinks(links) {
    this.datumLinks = links
  }


  /**
   * Add a GlassBaseHole object with an offset to the editing cutout
   */
  addHoleWithOffset(hole, offset) {
    let shapeDivisions = Config.Settings.arcSubdivisions

    this.holeInfoList.push({
      holeObj: hole,
      offset: new THREE.Vector2(offset.x, offset.y)
    })

    var vertices = []

    this.bounds.makeEmpty()

    // Rebuild vertex list
    this.holeInfoList.forEach(holeInfo => {
      var shapePath = holeInfo.holeObj.generateShape()
      let shapePoints = shapePath.getPoints(shapeDivisions)
      for(var i = 0; i < shapePoints.length-1; i++) {
        vertices.push(
          shapePoints[i].x + holeInfo.offset.x,
          shapePoints[i].y + holeInfo.offset.y,
          0,
          shapePoints[(i + 1) % shapePoints.length].x + holeInfo.offset.x,
          shapePoints[(i + 1) % shapePoints.length].y + holeInfo.offset.y,
          0
        )

        // Update bounds
        this.bounds.expandByPoint(new THREE.Vector2(shapePoints[i].x + holeInfo.offset.x, shapePoints[i].y + holeInfo.offset.y))
        this.bounds.expandByPoint(new THREE.Vector2(shapePoints[(i + 1) % shapePoints.length].x + holeInfo.offset.x, shapePoints[(i + 1) % shapePoints.length].y + holeInfo.offset.y))
      }
    })
    

    // Build the shape object from the hole shape data
    if(!this.outlineGeom || !this.shapeObj) {
      this.outlineGeom = new THREE.BufferGeometry()
      this.outlineGeom.setAttribute( 'position', new THREE.Float32BufferAttribute(vertices, 3))

      var outerMaterial = new THREE.LineBasicMaterial({
        color: this.editor.getTheme().editingOutlineColour,
        transparent: true,
        opacity: this.editor.getTheme().editingOutlineOpacity
      })
      this.shapeObj = new THREE.LineSegments(this.outlineGeom, outerMaterial)
      this.sceneObject.add(this.shapeObj)
    } else {
      this.shapeObj.geometry.setAttribute( 'position', new THREE.Float32BufferAttribute(vertices, 3))
      this.shapeObj.geometry.computeBoundingBox()
      this.shapeObj.geometry.computeBoundingSphere()
    }
  }


  /**
   * Build the cutout info from a GlassBaseHole object containing the shape of the cutout
   */
  buildFromHole(hole) {
    this.clear()
    this.addHoleWithOffset(hole, new THREE.Vector2(0, 0))
  }


  /**
   * Update the cutout drawing geometry from a GlassBaseHole object
   */
  updateFromHole(hole) {
    // Clear & re-add the hole
    // This function is only used for corner/edge cutouts and will not have multiple cutouts in the list
    this.holeInfoList = []
    this.addHoleWithOffset(hole, new THREE.Vector2(0, 0))
  }


  /**
   * Get the bounds of the editing cutout
   */
  calculateBounds() {
    return this.bounds.clone()
  }


  clear() {
    if(this.shapeObj)
      this.sceneObject.remove(this.shapeObj)
    if(this.outlineGeom)
      this.outlineGeom.dispose()

    this.shapeObj = null
    this.outlineGeom = null

    this.holeInfoList = []
    this.bounds.makeEmpty()
  }


  /**
   * Calculate the segments used to represent the editing cutout including position & rotation
   * Used for clipping operations
   */
  generateSegmentList() {
    if(this.holeInfoList.length == 0)
      return []

    var matrix = new THREE.Matrix4()
    var rotMatrix = new THREE.Matrix4()
    var quaternion = new THREE.Quaternion()
    quaternion.setFromEuler(new THREE.Euler(0, 0, MathsUtils.degreesToRadians(this.angle)))

    matrix.compose(new THREE.Vector3(this.position.x, this.position.y, 0), quaternion, new THREE.Vector3(1, 1, 1))
    matrix.scale(new THREE.Vector3(this.scale.x, this.scale.y, 1.0))
    
    rotMatrix.compose(new THREE.Vector3(0, 0, 0), quaternion, new THREE.Vector3(1, 1, 1))
    rotMatrix.scale(new THREE.Vector3(this.scale.x, this.scale.y, 1.0))

    var vec = new THREE.Vector3()

    // Only generate from first hole
    // Edge/corner clipping routines only ever use 1 hole
    var segmentList = this.holeInfoList[0].holeObj.segmentList.map(segment => {
      var outSegment = segment.clone()

      // Rotate around point
      vec.set(outSegment.startPosition.x, outSegment.startPosition.y, 0)
      vec.applyMatrix4(matrix)
      outSegment.startPosition.set(vec.x, vec.y)

      vec.set(outSegment.endPosition.x, outSegment.endPosition.y, 0)
      vec.applyMatrix4(matrix)
      outSegment.endPosition.set(vec.x, vec.y)

      // Arc info
      if(outSegment.type == GlassDesignerTypes.SegmentTypes.arc) {
        vec.set(outSegment.arcCenterPosition.x, outSegment.arcCenterPosition.y, 0)
        vec.applyMatrix4(matrix)
        outSegment.arcCenterPosition.set(vec.x, vec.y)

        outSegment.arcStartAngle = MathsUtils.calculateArcPointAngle(outSegment.arcCenterPosition, outSegment.startPosition)
        outSegment.arcEndAngle = MathsUtils.calculateArcPointAngle(outSegment.arcCenterPosition, outSegment.endPosition)

        // Flip clockwise flag if flipped scale
        if(this.scale.x < 0)
          outSegment.arcClockwise = !outSegment.arcClockwise

        outSegment.arcUpdated()
      }

      // Corner radius info
      vec.set(outSegment.startCornerRadiusDelta.x, outSegment.startCornerRadiusDelta.y, 0)
      vec.applyMatrix4(rotMatrix)
      outSegment.startCornerRadiusDelta.set(vec.x, vec.y)
      vec.set(outSegment.endCornerRadiusDelta.x, outSegment.endCornerRadiusDelta.y, 0)
      vec.applyMatrix4(rotMatrix)
      outSegment.endCornerRadiusDelta.set(vec.x, vec.y)

      return outSegment
    })

    return segmentList
  }


}
